import { useEffect, useState, useCallback } from 'react';
import { WifiHigh, WifiSlash, ArrowsClockwise, Check } from '@phosphor-icons/react';
import API from '@/lib/api';
import {
  isOnline,
  setupOnlineListener,
  flushQueue,
  getPendingCount,
  onQueueChange,
  getQueue,
  deleteQueueItem,
} from '@/lib/offlineStore';
import {
  leesQueue as leesBestelQueue,
  syncQueue as syncBestelQueue,
  verwijderUitQueue as verwijderBestelItem,
} from '@/lib/offlineQueue';
import { toast } from 'sonner';

/**
 * Compact online/offline-status indicator met queue-counter.
 * - Online + 0 pending → klein groen check icoon
 * - Online + N pending → blauw "syncing X items..." met spinner
 * - Offline → rood "offline" badge, klik → modal met queue-overzicht
 */
export default function OfflineStatus() {
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [items, setItems] = useState([]);

  const refreshCount = useCallback(async () => {
    try {
      const c = await getPendingCount();
      const legacy = leesBestelQueue().length;
      setPending(c + legacy);
    } catch (_e) { /* niks */ }
  }, []);

  // ── Combineerde sync: IndexedDB-queue + legacy localStorage-queue
  const runFullSync = useCallback(async () => {
    setSyncing(true);
    let totalSent = 0, totalFailed = 0;
    try {
      const r1 = await flushQueue(API);
      totalSent += r1.sent || 0;
      totalFailed += r1.failed || 0;
    } catch { /* noop */ }
    try {
      const r2 = await syncBestelQueue(API);
      totalSent += r2.gesync || 0;
      totalFailed += (r2.fouten || []).length;
    } catch { /* noop */ }
    setSyncing(false);
    await refreshCount();
    return { sent: totalSent, failed: totalFailed };
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    // Poll de localStorage-queue elke 5s (geen native event)
    const poll = setInterval(refreshCount, 5000);
    const cleanup = setupOnlineListener(
      async () => {
        setOnline(true);
        // Probeer queue te flushen na 1s
        setTimeout(async () => {
          const r = await runFullSync();
          if (r.sent > 0) toast.success(`✅ ${r.sent} actie${r.sent === 1 ? '' : 's'} gesynchroniseerd`);
          if (r.failed > 0) toast.warning(`⚠️ ${r.failed} actie${r.failed === 1 ? '' : 's'} mislukt`);
        }, 800);
      },
      () => setOnline(false)
    );
    const unsub = onQueueChange(refreshCount);
    // Luister naar legacy localStorage-queue changes (instant ipv via poll)
    const onLegacyChange = () => refreshCount();
    window.addEventListener('verkoop-queue-change', onLegacyChange);
    // Luister naar service-worker postMessage 'online-sync'
    const onSwMsg = (e) => { if (e.data?.type === 'online-sync') runFullSync(); };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener?.('message', onSwMsg);
    }
    return () => {
      clearInterval(poll);
      cleanup();
      unsub();
      window.removeEventListener('verkoop-queue-change', onLegacyChange);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener?.('message', onSwMsg);
      }
    };
  }, [refreshCount, runFullSync]);

  const openModal = async () => {
    const idbItems = await getQueue();
    const legacyItems = leesBestelQueue().map(it => ({
      id: `legacy:${it.local_id}`,
      legacy_id: it.local_id,
      label: `Bestelbon (offline) — €${(it.payload?.totaal || 0).toFixed(2)}`,
      url: '/documenten',
      kind: 'bestelbon',
      status: (it.retries || 0) >= 5 ? 'failed' : 'pending',
      createdAt: it.aangemaakt,
      attempts: it.retries || 0,
      lastError: it.laatste_fout,
    }));
    setItems([...idbItems, ...legacyItems]);
    setShowModal(true);
  };

  const handleManualSync = async () => {
    if (!navigator.onLine) {
      toast.error('Geen internet — wacht tot je terug online bent');
      return;
    }
    const r = await runFullSync();
    const idbItems = await getQueue();
    const legacyItems = leesBestelQueue().map(it => ({
      id: `legacy:${it.local_id}`,
      legacy_id: it.local_id,
      label: `Bestelbon (offline) — €${(it.payload?.totaal || 0).toFixed(2)}`,
      url: '/documenten',
      kind: 'bestelbon',
      status: (it.retries || 0) >= 5 ? 'failed' : 'pending',
      createdAt: it.aangemaakt,
      attempts: it.retries || 0,
      lastError: it.laatste_fout,
    }));
    setItems([...idbItems, ...legacyItems]);
    if (r.sent > 0) toast.success(`${r.sent} acties gesynchroniseerd`);
    if (r.failed > 0) toast.warning(`${r.failed} acties mislukt — probeer later opnieuw`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Pending actie verwijderen? Deze gaat verloren.')) return;
    if (typeof id === 'string' && id.startsWith('legacy:')) {
      const localId = id.slice('legacy:'.length);
      verwijderBestelItem(localId);
    } else {
      await deleteQueueItem(id);
    }
    await refreshCount();
    const idbItems = await getQueue();
    const legacyItems = leesBestelQueue().map(it => ({
      id: `legacy:${it.local_id}`,
      legacy_id: it.local_id,
      label: `Bestelbon (offline) — €${(it.payload?.totaal || 0).toFixed(2)}`,
      url: '/documenten',
      kind: 'bestelbon',
      status: (it.retries || 0) >= 5 ? 'failed' : 'pending',
      createdAt: it.aangemaakt,
      attempts: it.retries || 0,
      lastError: it.laatste_fout,
    }));
    setItems([...idbItems, ...legacyItems]);
  };

  // ─── Render ─────────────────────────────────────────
  // Verberg helemaal wanneer alles ok is
  if (online && pending === 0 && !syncing) return null;

  let label, bg, icon;
  if (!online) {
    label = pending > 0 ? `Offline · ${pending} wachtrij` : 'Offline';
    bg = 'bg-red-500';
    icon = <WifiSlash size={14} weight="bold" />;
  } else if (syncing) {
    label = `Synchroniseren${pending ? ` (${pending})` : ''}...`;
    bg = 'bg-blue-500';
    icon = <ArrowsClockwise size={14} weight="bold" className="animate-spin" />;
  } else if (pending > 0) {
    label = `${pending} wachten op sync`;
    bg = 'bg-amber-500';
    icon = <ArrowsClockwise size={14} weight="bold" />;
  } else {
    label = 'Online';
    bg = 'bg-emerald-500';
    icon = <Check size={14} weight="bold" />;
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`fixed bottom-4 right-4 z-40 ${bg} text-white px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 shadow-lg hover:opacity-90 transition-all`}
        data-testid="offline-status-badge"
      >
        {icon}
        {label}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="offline-status-modal">
            <div className={`${online ? 'bg-emerald-500' : 'bg-red-500'} text-white p-4 sm:rounded-t-2xl flex items-center gap-2`}>
              {online ? <WifiHigh size={22} weight="duotone" /> : <WifiSlash size={22} weight="duotone" />}
              <div className="flex-1">
                <h2 className="font-bold">{online ? 'Online — sync-status' : 'Offline modus'}</h2>
                <p className="text-xs opacity-90">{online
                  ? 'Acties die offline werden gemaakt'
                  : 'Acties worden lokaal opgeslagen tot terug verbinding'}</p>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-white/80 hover:text-white">✕</button>
            </div>

            <div className="p-4">
              {items.length === 0 ? (
                <div className="text-center text-slate-500 py-6">
                  <Check size={32} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
                  Wachtrij is leeg — alles is gesynchroniseerd.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map(it => (
                    <li key={it.id} className="py-2 flex items-center gap-3" data-testid={`offline-queue-item-${it.id}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        it.status === 'pending' ? 'bg-amber-500' :
                        it.status === 'failed' ? 'bg-red-500' : 'bg-emerald-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{it.label || it.url}</div>
                        <div className="text-[11px] text-slate-500">
                          {new Date(it.createdAt).toLocaleString('nl-BE')}
                          {it.attempts > 0 && ` · ${it.attempts} pogingen`}
                          {it.lastError && ` · ${it.lastError.slice(0, 60)}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(it.id)}
                        className="text-xs text-slate-400 hover:text-red-600 px-2"
                        title="Verwijderen"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {online && items.length > 0 && (
                <button
                  type="button"
                  onClick={handleManualSync}
                  disabled={syncing}
                  className="w-full mt-4 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-semibold inline-flex items-center justify-center gap-2"
                >
                  {syncing ? <ArrowsClockwise size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />}
                  Synchroniseer nu
                </button>
              )}
              {!online && (
                <div className="mt-4 text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                  💡 Je bent offline. Zodra je terug verbinding hebt, worden alle wachtende acties automatisch verstuurd.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
